import { useEffect, useState } from "react";
import { Btn } from "../../utils/ui.jsx";

// Confirm dialog for deactivating / reactivating a user. When deactivating a
// shelter's LAST active worker, it warns by shelter name and offers to also
// deactivate the now-orphaned shelter(s) (decision 1 in
// docs/account-shelter-lifecycle-plan.md). `orphanedShelters` is computed by the
// caller via assessUserDeactivation: [{ id, shelter_code, shelter_name }].
export function DeactivateUserConfirmModal({ user, onClose, onConfirm, orphanedShelters = [] }) {
  const [alsoDeactivate, setAlsoDeactivate] = useState(false);
  // Reset the opt-in each time a different user's dialog opens.
  useEffect(() => {
    setAlsoDeactivate(false);
  }, [user?.id]);

  if (!user) return null;

  const isReactivating = user.deactivated;
  const who = user.display_name || user.shelter_name;
  // Only warn when actually deactivating (not when reactivating).
  const orphans = isReactivating ? [] : orphanedShelters;
  const hasOrphans = orphans.length > 0;
  const shelterList = orphans.map((s) => s.shelter_name || s.shelter_code).join(", ");

  return (
    <div className="fixed inset-0 bg-black/45 z-[9999] flex items-center justify-center p-5">
      <div className="bg-white rounded-[14px] max-w-[400px] w-full p-6 shadow-[0_8px_40px_rgba(0,0,0,0.2)]">
        <div className="font-bold text-base mb-[10px]">
          {isReactivating ? "Reactivate User?" : "Deactivate User?"}
        </div>
        <div className="text-[13px] text-zephyr-muted mb-5">
          {isReactivating
            ? `This will restore login access for ${who}.`
            : `This will prevent ${who} from logging in. Their data will be preserved.`}
        </div>

        {hasOrphans && (
          <div className="mb-5 rounded-[8px] border border-[#E0A100] bg-[#FFF8E1] p-3">
            <div className="text-[13px] font-bold text-[#8A6D00] mb-1">⚠️ Last active worker</div>
            <div className="text-[12px] text-[#5F4A00] mb-3">
              {who} is the last active worker for{" "}
              {orphans.length > 1 ? "these DFV Services" : "this DFV Service"}:{" "}
              <span className="font-semibold">{shelterList}</span>. Deactivating{" "}
              {orphans.length > 1 ? "them" : "it"} leaves no one able to submit requests for{" "}
              {orphans.length > 1 ? "those DFV Services" : "that DFV Service"}.
            </div>
            <label className="flex items-start gap-2 text-[12px] text-[#5F4A00] cursor-pointer">
              <input
                type="checkbox"
                checked={alsoDeactivate}
                onChange={(e) => setAlsoDeactivate(e.target.checked)}
                className="mt-[2px]"
              />
              <span>
                Also deactivate {orphans.length > 1 ? "these DFV Services" : "this DFV Service"} (
                {shelterList})
              </span>
            </label>
          </div>
        )}

        <div className="flex gap-[10px] justify-end">
          <Btn variant="secondary" onClick={onClose}>
            Cancel
          </Btn>
          {/* background is runtime-conditional — keep inline for that value only */}
          <Btn
            onClick={function () {
              onConfirm(user, { alsoDeactivateShelters: hasOrphans && alsoDeactivate });
            }}
            style={{
              background: isReactivating ? "#00897B" : "#C62828",
              color: "white",
            }}
          >
            {isReactivating ? "Reactivate" : "Deactivate"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
